import { WEBSITE_URL, getCanonicalUrl } from "@/content";
import { projectDisplayItems } from "@/content/projects/project-display";
import { getProjectCaseStudyBySlug, projectCaseStudySlugs } from "@/content/projects/project-case-studies";
import { getProjectPartnerDirectoryBySlug } from "@/content/projects/project-partners";
import type { MetaTagsProps } from "@/types";
import { PartnerDirectory, ProjectDisplay, WebCaseStudy, type ProjectDisplayItem } from "@modes0/ui-components/client";
import type { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import { PageSeo, buildBreadcrumbSchema, buildCreativeWorkSchema } from "@modes0/ui-components-seo";
import { getBreadcrumbSeoItems, seoSiteProfile } from "@/content/seo";

type ProjectCaseStudyPageProps = {
    projectSlug: string;
};

const projectCaseStudySliderItems: ProjectDisplayItem[] = projectDisplayItems.map((project) => ({
    ...project,
    tags: [],
    services: [],
}));

const toAbsoluteImageUrl = (src: string) => {
    if (/^https?:\/\//i.test(src)) {
        return src;
    }

    return `${WEBSITE_URL}${src.startsWith("/") ? src : `/${src}`}`;
};

export const getStaticPaths: GetStaticPaths = () => ({
    paths: projectCaseStudySlugs.map((projectSlug) => ({
        params: {
            projectSlug,
        },
    })),
    fallback: false,
});

export const getStaticProps: GetStaticProps<ProjectCaseStudyPageProps> = ({ params }) => {
    const projectSlug = String(params?.projectSlug || "");

    if (!getProjectCaseStudyBySlug(projectSlug)) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            projectSlug,
        },
    };
};

export default function ProjectCaseStudyPage({ projectSlug }: ProjectCaseStudyPageProps) {
    const caseStudy = getProjectCaseStudyBySlug(projectSlug);
    const partnerDirectory = getProjectPartnerDirectoryBySlug(projectSlug);

    if (!caseStudy) {
        return null;
    }

    const metaTags: MetaTagsProps = {
        ...caseStudy.meta,
        url: getCanonicalUrl(caseStudy.meta.path),
        ogImage: toAbsoluteImageUrl(caseStudy.meta.imagePath),
    };

    return (
        <>
            <PageSeo
                meta={metaTags}
                siteProfile={seoSiteProfile}
                schemas={[
                    {
                        id: `${projectSlug}-creative-work-json-ld`,
                        schema: buildCreativeWorkSchema(
                            {
                                title: caseStudy.meta.title,
                                description: caseStudy.meta.description,
                                url: getCanonicalUrl(caseStudy.meta.path),
                                image: caseStudy.meta.imagePath,
                            },
                            seoSiteProfile
                        ),
                    },
                    {
                        id: `${projectSlug}-breadcrumb-json-ld`,
                        schema: buildBreadcrumbSchema(
                            getBreadcrumbSeoItems([
                                { name: "Home", url: getCanonicalUrl() },
                                { name: "Projects", url: getCanonicalUrl("/projects") },
                                { name: caseStudy.project.title, url: getCanonicalUrl(caseStudy.meta.path) },
                            ]),
                            seoSiteProfile
                        ),
                    },
                ]}
            />
            <WebCaseStudy Link={Link} {...caseStudy.componentProps} />
            {partnerDirectory ? <PartnerDirectory {...partnerDirectory} Link={Link} /> : null}
            <ProjectDisplay
                Link={Link}
                items={projectCaseStudySliderItems}
                layout="slide"
                className="project-case-study-project-display"
                eyebrow="Our Work"
                title={
                    <>
                        More Projects <span className="project-display-title-highlight">Built to Grow.</span>
                    </>
                }
                description="Browse the full Grow Leads project list across websites, SEO, Google setup, automation, retail, trades and media."
                showFilters={false}
                ctaLabel="View Project"
                accentColor="#2faa2e"
                backgroundColor="#ffffff"
                surfaceColor="#ffffff"
                colors={{
                    sectionBorder: "rgba(16, 24, 40, 0.1)",
                }}
                maxWidth={1440}
                tileWidth={212}
                minTileWidth={216}
                imageHeight={190}
                ariaLabel="All Grow Leads client projects"
                style={{ padding: "64px 16px" }}
            />
        </>
    );
}
